import React, {
  ReactElement,
  useCallback,
  useEffect, useRef,
  useState,
} from 'react';
import {
  Formik,
  Form,
  FormikConfig,
  FormikProps,
  FormikValues,
} from 'formik';
import { Grid } from '@material-ui/core';
import { Schema } from 'yup';

import Spinner from '../spinner/Spinner';
import { ItemFormContext, useItemFormContext } from './ItemFormContext';
import ItemFormControls, { CONTROLS_CONTAINER_HEIGHT } from './ItemFormControls';
import useStyles from './styles';
import CloseTabItemFormDetector from './CloseTabItemFormDetector';

export type FormRenderProps<T extends FormikValues> = FormikProps<T>;

type Props<T extends FormikValues> = {
  loading: boolean;
  creatingMode?: boolean;
  editPermitted?: boolean;
  validationSchema: Schema<unknown>;
  initialValues: T | null;
  onSubmit: (values: T) => void;
  titleSubmit?: string;
  hideActions?: boolean;
  preventCancelChanges?: boolean;
  validateOnMount?: boolean;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  formStyles?: string;
} & Omit<FormikConfig<T>, 'initialValues' | 'onSubmit'>;

const ItemForm = <T extends FormikValues>({
  loading,
  creatingMode = false,
  children,
  initialValues,
  titleSubmit,
  hideActions = false,
  preventCancelChanges = false,
  validateOnMount = false,
  validateOnBlur = true,
  validateOnChange = false,
  formStyles,
  ...props
}: Props<T>): ReactElement | null => {
  const classes = useStyles();
  const itemFormContext = useItemFormContext();
  const containerRef = useRef(null);
  const [isBottom, setIsBottom] = useState(false);

  const checkFormOutOfView = useCallback(() => {
    const el = containerRef.current;
    if (!loading && el) {
      // @ts-ignore
      const boundObj = el.getBoundingClientRect();

      if (boundObj.bottom + CONTROLS_CONTAINER_HEIGHT > (window.innerHeight || document.documentElement.clientHeight)) {
        setIsBottom(false);
      } else {
        setIsBottom(true);
      }
    }
  }, [containerRef, loading, setIsBottom]);

  const checkScrollEnd = useCallback(() => {
    const docEl = document.documentElement;
    setIsBottom((docEl.scrollHeight - docEl.scrollTop < docEl.clientHeight + 100 - 2));
    if (docEl.scrollHeight === docEl.clientHeight) setIsBottom(true);
    if (docEl.scrollHeight - docEl.clientHeight < 100) {
      setIsBottom(true);
    }
    if (docEl.scrollTop === 0) setIsBottom(false);

    checkFormOutOfView();
  }, [setIsBottom, checkFormOutOfView]);

  useEffect(() => {
    checkScrollEnd();
  }, [checkScrollEnd]);

  // check if form go out of bottom
  useEffect(() => {
    checkFormOutOfView();
  }, [checkFormOutOfView]);

  useEffect(() => {
    window.addEventListener('scroll', checkScrollEnd);
    window.addEventListener('resize', checkScrollEnd);
    return () => {
      window.removeEventListener('scroll', checkScrollEnd);
      window.removeEventListener('resize', checkScrollEnd);
    };
  }, [setIsBottom, isBottom, checkScrollEnd]);

  if (loading) {
    return (
      <Grid container className={classes.spinnerWrapper}>
        <Spinner />
      </Grid>
    );
  }

  if (initialValues === null) {
    return null;
  }

  const { disableSubmit } = itemFormContext;

  return (
    <ItemFormContext.Provider value={itemFormContext}>
      <Formik<T, {}>
        initialValues={initialValues}
        {...props}
        validateOnMount={validateOnMount}
        validateOnBlur={validateOnBlur}
        validateOnChange={validateOnChange}
      >
        {(formProps) => (
          <Grid ref={containerRef} container className={`${classes.root} ${formStyles}`}>
            <Form onSubmit={formProps.handleSubmit} className={classes.form}>
              <CloseTabItemFormDetector />
              {typeof children === 'function' ? children(formProps) : children}
              {((formProps.dirty || creatingMode) && !loading && !hideActions) && (
                <ItemFormControls
                  preventCancelChanges={preventCancelChanges}
                  postText={titleSubmit || (creatingMode ? 'Create' : 'Save')}
                  loading={loading}
                  dirty={formProps.dirty}
                  creatingMode={creatingMode}
                  disableSubmit={disableSubmit}
                  isSubmitting={formProps.isSubmitting}
                  position={isBottom ? 'relative' : 'fixed'}
                />
              )}
            </Form>
          </Grid>
        )}
      </Formik>
    </ItemFormContext.Provider>
  );
};

export default ItemForm;
